import { useCallback, useContext, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';

import WarningConfirmModal from '../WarningConfirmModal';
import { taytanFormatNumberToString } from '../../utils/mathUtils';
import { Context as CurrenciesContext } from '../../api/currencies/context';
import type { OverBalanceInfo } from './overBalance';

export { EXCEEDS_BALANCE_CODE, readOverBalance, type OverBalanceInfo } from './overBalance';

/**
 * The warning before charging more than a travel file still owes. The server
 * refuses such a charge until the agent confirms here; the service team
 * sometimes needs to take an extra payment on a booking already paid in full.
 *
 *   const { confirmOverBalance, overBalanceDialog } = useOverBalanceConfirm();
 *   if (await confirmOverBalance(info)) retryWithOverride();
 *   ...
 *   return <>{overBalanceDialog}...</>;
 */
export const useOverBalanceConfirm = () => {
    const [info, setInfo] = useState<OverBalanceInfo | null>(null);
    const currenciesContext = useContext(CurrenciesContext);
    const resolveRef = useRef<((ok: boolean) => void) | null>(null);

    const settle = useCallback((ok: boolean) => {
        resolveRef.current?.(ok);
        resolveRef.current = null;
        setInfo(null);
    }, []);

    const confirmOverBalance = useCallback(
        (next: OverBalanceInfo) =>
            new Promise<boolean>((resolve) => {
                resolveRef.current?.(false);
                resolveRef.current = resolve;
                setInfo(next);
            }),
        []
    );

    // "5,680.00$" — the symbol after the figure, as the rest of the app writes it.
    const money = (value?: number) => {
        if (value == null) return '';
        const symbol =
            currenciesContext.dataAsArray.find((c) => String(c.code).toUpperCase() === String(info?.currency).toUpperCase())
                ?.symbol ?? (info?.currency ? ` ${info.currency}` : '');
        return `${taytanFormatNumberToString(value)}${symbol}`;
    };
    const paidInFull = info?.balance != null && info.balance <= 0;
    // Asked as the receipt opens, before any amount is entered.
    const beforeForm = info?.amount == null;
    const overBalanceDialog = (
        <WarningConfirmModal
            open={!!info}
            toggleModal={() => settle(false)}
            onConfirm={() => settle(true)}
            confirmText={beforeForm ? 'Continue' : 'Charge anyway'}
            title={
                paidInFull
                    ? `This travel file is already paid in full - ${money(info?.paid)}`
                    : `This travel file only owes ${money(info?.balance)}`
            }
        >
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
                {beforeForm
                    ? 'Issue an extra receipt anyway?'
                    : paidInFull
                      ? `Charge an extra ${money(info?.amount)} on the card anyway?`
                      : `Charge ${money(info?.amount)} on the card anyway?`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.5 }}>
                After the payment, raise the service price by the same amount so the balance returns to 0.
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 1.5, fontStyle: 'italic' }}
            >
                The extra payment is recorded on the travel file&apos;s log.
            </Typography>
        </WarningConfirmModal>
    );

    return { confirmOverBalance, overBalanceDialog, isAskingOverBalance: !!info };
};
